import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import type { DsIcon } from '@/ds/icons'

export type DsBadgeVariant = 'soft' | 'solid' | 'outline'
export type DsBadgeSize = 'xs' | 'sm' | 'md'

const sizeCls: Record<DsBadgeSize, string> = {
  xs: 'h-4 px-1 text-[10px] leading-none font-semibold tracking-[0.6px]',
  sm: 'h-[22px] px-2 text-caption font-semibold',
  md: 'h-[26px] px-2.5 text-small font-semibold',
}

export interface DsBadgeProps {
  label: ReactNode
  tone?: DsTone
  variant?: DsBadgeVariant
  size?: DsBadgeSize
  icon?: DsIcon
  leadingDot?: boolean
  className?: string
}

function BadgeBase({ label, tone = 'neutral', variant = 'soft', size = 'sm', icon, leadingDot, className }: DsBadgeProps) {
  const t = toneClass[tone]
  const look =
    variant === 'solid' ? cn(t.solid, t.onSolid) : variant === 'outline' ? cn('border bg-transparent', t.border, t.text) : cn(t.soft, t.onSoft)
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full leading-none', sizeCls[size], look, className)}>
      {leadingDot && <span className={cn('size-1.5 rounded-full', variant === 'solid' ? 'bg-current' : t.vivid)} aria-hidden />}
      {icon && <DsIconGlyph icon={icon} size={size === 'xs' ? 10 : 12} />}
      {label}
    </span>
  )
}

/** Count badge for bells and tabs: `99+` above max. */
function Count({ value, max = 99, tone = 'danger', variant = 'solid', size = 'sm', semanticLabel }: { value: number; max?: number; tone?: DsTone; variant?: DsBadgeVariant; size?: DsBadgeSize; semanticLabel?: string }) {
  if (value <= 0) return null
  return (
    <span aria-label={semanticLabel ?? `${value}`}>
      <BadgeBase label={value > max ? `${max}+` : value} tone={tone} variant={variant} size={size} className="min-w-4 justify-center" />
    </span>
  )
}

function Dot({ tone = 'danger', size = 'sm', semanticLabel }: { tone?: DsTone; size?: DsBadgeSize; semanticLabel?: string }) {
  return <span role={semanticLabel ? 'img' : undefined} aria-label={semanticLabel} aria-hidden={!semanticLabel} className={cn('inline-block rounded-full', toneClass[tone].vivid, size === 'xs' ? 'size-1.5' : size === 'sm' ? 'size-2' : 'size-2.5')} />
}

/** Tone + text, never colour alone. */
export const DsBadge = Object.assign(BadgeBase, { Count, Dot })

export function DsBadged({ children, badge, className }: { children: ReactNode; badge: ReactNode; className?: string }) {
  return (
    <span className={cn('relative inline-flex', className)}>
      {children}
      <span className="absolute -right-1.5 -top-1.5">{badge}</span>
    </span>
  )
}
